import React from 'react';
import PropTypes from 'prop-types';
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import {fieldObjectSectionStyle} from "../../helpers/graphQLShortcuts";
import {fieldObjectSectionStyleCss} from "../../helpers/createStyles";
import Paragraph from '../Paragraph';
import {filterHeadline, filterHTML} from "../../helpers/filters";
import './BioCards.scss';

interface Data {
    paragraphById: {
        fieldInnerBioItems: Array<>;
        fieldInnerBiosSectionHeader: Array<{
            entity: Array<{
                fieldTxtHeadline: string;
                fieldHtmlBody: Array<{
                    value: string;
                    format: string;
                    processed: string;
                }>;
                fieldVarTextAlignment: string;
            }>
        }>;
        fieldInnerBiosSectionFooter: Array<{
            entity: Array<{
                fieldTxtHeadline: string;
                fieldHtmlBody: Array<{
                    value: string;
                    format: string;
                    processed: string;
                }>;
                fieldObjectCta: Array<{targetRevisionId: string}>;
            }>
        }>;
        fieldInnerBiosSectionStyle: Array<{entity: Array}>;
    }
}

class BioCardsQuery extends Query<Data, {id: string}> {}

const GET_CARDS = gql`
    query paragraphById($id: String!) {
        paragraphById:paragraphRevisionById(id: $id) {
            ... on ParagraphComponentBioCards {
                fieldInnerBioItems {
                    targetRevisionId
                }
                fieldInnerBiosSectionHeader {
                    ... on FieldParagraphFieldInnerBiosSectionHeader {
                        entity {
                            ... on Paragraph {
                                ... on ParagraphInnerSectionHeader {
                                    fieldTxtHeadline
                                    fieldHtmlBody {
                                        value
                                        format
                                        processed
                                    }
                                    fieldVarTextAlignment
                                }
                            }
                        }
                    }
                }
                fieldInnerBiosSectionFooter {
                    ... on FieldParagraphFieldInnerBiosSectionFooter {
                        entity {
                            ... on Paragraph {
                                ... on ParagraphInnerSectionFooter {
                                    fieldTxtHeadline
                                    fieldHtmlBody {
                                        value
                                        format
                                        processed
                                    }
                                    fieldObjectCta {
                                        targetRevisionId
                                    }
                                }
                            }
                        }
                    }
                }
                fieldInnerBiosSectionStyle {
                    ... on FieldParagraphFieldInnerBiosSectionStyle {
                        ${fieldObjectSectionStyle}
                    }
                }
            }
        }
    }
`;

const BioCards = ({id, markComponentLoadedAs, handleLoadedComponents}) => (
    <BioCardsQuery asyncMode query={GET_CARDS} variables={{ id }}>
        {({ loading, error, data }) => {
            if (loading) return null;
            if (error) {
                markComponentLoadedAs(id, true);
                return <div>{error}</div>;
            }

            const field = data.paragraphById;
            const header = field.fieldInnerBiosSectionHeader ? field.fieldInnerBiosSectionHeader.entity : null;
            const footer = field.fieldInnerBiosSectionFooter ? field.fieldInnerBiosSectionFooter.entity : null;

            const styles = fieldObjectSectionStyleCss(field.fieldInnerBiosSectionStyle);

            if (!styles.css.padding) {
                styles.css.padding = '80px 20px';
            }

            markComponentLoadedAs(id, true);

            return (
                <div className={`bioCards${styles.classNames}`} style={styles.css} id={styles.id}>
                    {styles.childBackground.isActive && (
                        <div style={styles.childBackground.css}/>
                    )}
                    <div className="container">
                        {header && ((header.fieldTxtHeadline) || (header.fieldHtmlBody && header.fieldHtmlBody.processed)) && (
                            <div className="bioCards__header">
                                {header.fieldTxtHeadline && (
                                    // eslint-disable-next-line react/no-danger
                                    <h2 dangerouslySetInnerHTML={{__html: filterHeadline(header.fieldTxtHeadline)}} />
                                )}

                                {header.fieldHtmlBody && header.fieldHtmlBody.processed && (
                                    // eslint-disable-next-line react/no-danger
                                    <div dangerouslySetInnerHTML={{__html: filterHTML(header.fieldHtmlBody.processed)}}/>
                                )}
                            </div>
                        )}

                        <div className="grid grid--items-4">
                            {field.fieldInnerBioItems.map(component => (
                                <Paragraph key={`${id}_${component.targetRevisionId}`}
                                           id={String(component.targetRevisionId)}
                                           handleLoadedComponents={handleLoadedComponents}/>
                            ))}
                        </div>

                        {footer && ((footer.fieldTxtHeadline) || (footer.fieldObjectCta && footer.fieldObjectCta.length) || (footer.fieldHtmlBody && footer.fieldHtmlBody.processed)) && (
                            <div className="bioCards__footer">
                                {footer.fieldTxtHeadline && (
                                    // eslint-disable-next-line react/no-danger
                                    <h4 dangerouslySetInnerHTML={{__html: filterHeadline(footer.fieldTxtHeadline)}} />
                                )}
                                {footer.fieldHtmlBody && footer.fieldHtmlBody.processed && (
                                    // eslint-disable-next-line react/no-danger
                                    <div dangerouslySetInnerHTML={{__html: filterHTML(footer.fieldHtmlBody.processed)}}/>
                                )}
                                {footer.fieldObjectCta && footer.fieldObjectCta.map(component => (
                                    <Paragraph key={`${id}_${component.targetRevisionId}`}
                                               id={String(component.targetRevisionId)}
                                               handleLoadedComponents={handleLoadedComponents}/>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }}
    </BioCardsQuery>
);

BioCards.propTypes = {
    id: PropTypes.number.isRequired,
    markComponentLoadedAs: PropTypes.func.isRequired,
    handleLoadedComponents: PropTypes.func.isRequired
};

export default BioCards;



// WEBPACK FOOTER //
// ./frontend/components/BioCards/BioCards.jsx